import type { FC } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { cssMap } from '@compiled/react';

import Button from '@atlaskit/button/default/button';
import { token } from '@atlaskit/tokens';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { filterSuggestions, SuggestionFilterValues } from '../suggestionsPanelFilters.ts';
import { useSuggestionsPanelFilters } from '../useSuggestionsPanelFilters.ts';
import { useSuggestionsSource } from '../useSuggestionsSource.ts';

import { AiPolicyFooter } from './AiPolicyFooter.tsx';

const i18n = defineMessages({
	discardAll: {
		id: 'suggestions-panel.footer.discard-all',
		defaultMessage: 'Discard all',
		description:
			'Button to immediately discard all AI suggestions matching the current panel filter',
	},
});

const styles = cssMap({
	footer: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		position: 'relative',
		zIndex: 1,
		width: '100%',
		paddingBlock: token('space.050'),
		paddingInline: token('space.250'),
		gap: token('space.050'),
	},
	scrim: {
		position: 'absolute',
		insetBlockStart: token('space.negative.200'),
		insetBlockEnd: 0,
		insetInlineStart: 0,
		insetInlineEnd: 0,
		zIndex: -1,
		backgroundImage: `linear-gradient(0deg, ${token('elevation.surface.overlay')} 80.78%, transparent 100%)`,
	},
});

/**
 * This component is only used under platform_editor_ai_content_suggested_edits
 * at the moment.
 */
export const SuggestionsFooter: FC = () => {
	const { formatMessage } = useIntl();
	const { suggestions, editorAPI } = useSuggestionsSource();
	const [{ currentFilter }] = useSuggestionsPanelFilters();
	const { userId } = useSessionData();
	const visibleSuggestions = filterSuggestions(suggestions, currentFilter, userId);

	const handleDiscardAll = () => {
		if (!visibleSuggestions.length) {
			return;
		}

		const actions = editorAPI?.aiSuggestions?.actions;
		if (currentFilter === SuggestionFilterValues.ALL) {
			actions?.discardAllSuggestions();
		} else {
			actions?.discardAllSuggestions(visibleSuggestions.map(({ id }) => id));
		}
	};

	return (
		<footer css={styles.footer} data-testid="suggestions-panel-footer">
			<div aria-hidden css={styles.scrim} />
			{editorAPI && visibleSuggestions.length > 0 && (
				<Button appearance="default" shouldFitContainer onClick={handleDiscardAll}>
					{formatMessage(i18n.discardAll)}
				</Button>
			)}
			<AiPolicyFooter />
		</footer>
	);
};
